import {useId} from '@mantine/hooks';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {MouseEventHandler, MouseEvent, ReactNode} from 'react';

import {UserSummary} from 'constants/UserConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {AvatarSize} from 'ui-library/avatar/AvatarConstants';
import Tooltip, {TooltipPlacement, TooltipWrapper} from 'ui-library/bs/Tooltip';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import * as nyc from 'ui-library/NYC';
import PartyAvatarList from 'ui-library/PartyAvatarList';

import StarIcon from '../StarIcon';

export interface SidepanelItemProps {
  active?: boolean;
  className?: string;
  dataTestAttrs?: DataTestAttrs;
  faded?: boolean;
  handleClickStar?: () => void;
  icon?: ReactNode;
  isCompact: boolean;
  onClick: MouseEventHandler;
  onContextMenu?: MouseEventHandler;
  onMouseEnter?: MouseEventHandler;
  onMouseLeave?: MouseEventHandler;
  parties?: Array<UserSummary>;
  showStar?: boolean;
  // optional element appearing on the right side, to the left of the star icon
  inlineContentRight?: ReactNode;
  starred?: boolean;
  subTitle?: ReactNode;
  title: {
    text: string;
    node?: ReactNode;
  };
  tooltipText?: string;
  isExpandable: boolean;
  isExpanded?: boolean;
  // Signifies if this is a child of an expandable item.
  isChild: boolean;
}

function SidepanelItem({
  active = false,
  className,
  dataTestAttrs,
  faded = false,
  handleClickStar,
  icon,
  isCompact,
  onClick,
  onContextMenu,
  onMouseEnter,
  onMouseLeave,
  parties = [],
  showStar = false,
  inlineContentRight,
  starred = false,
  subTitle,
  title,
  tooltipText,
  isExpandable,
  isExpanded,
  isChild,
}: SidepanelItemProps): JSX.Element {
  const titleId = useId();

  const onClickStar = (e: MouseEvent): void => {
    e.stopPropagation();
    handleClickStar?.();
  };

  const renderSubHeader = (): JSX.Element | null => {
    if (subTitle == null) {
      return null;
    }
    return (
      <span
        className="sidepanelItem-subTitle"
        {...getTestProps({component: 'SidepanelItem', element: 'subTitleSection'})}
      >
        <span className="sidepanelItem-subTitle-text">{subTitle}</span>
        {!isCompact && (
          <PartyAvatarList
            maxAvatars={3}
            parties={parties}
            size={AvatarSize.XXSMALL}
            tooltipPlacement={FloatingPlacement.BOTTOM}
            avatarTextDataTestAttrs={{component: 'SidepanelItem', element: 'overflowAvatarText'}}
          />
        )}
      </span>
    );
  };

  const subHeaderElement = renderSubHeader();
  let inlineElement: JSX.Element | null = null;
  // If `isCompact` is true and `showStar` is false,
  // `subHeaderElement` will be rendered inline here instead of below the header
  if (showStar) {
    inlineElement = (
      <span
        className={css(styles.inlineElement)}
        id="inlineElement"
        {...getTestProps({component: 'SidepanelItem', element: 'inlineElement'})}
      >
        {inlineContentRight ?? null}
        <StarIcon onClick={onClickStar} starred={starred} />
      </span>
    );
  } else if (isCompact) {
    inlineElement = subHeaderElement;
  }

  return (
    <TooltipWrapper tooltipTitle={tooltipText} tooltipPlacement={TooltipPlacement.RIGHT}>
      <div
        className={classNames('sidepanelItem', className, {
          'sidepanelItem--compact': isCompact,
          'sidepanelItem--faded': faded,
          'is-active': active,
        })}
        aria-labelledby={titleId}
        onClick={onClick}
        onContextMenu={onContextMenu}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        {...getTestProps(dataTestAttrs ?? {component: 'SidepanelItem', element: 'root'})}
      >
        <nyc.Row>
          <nyc.Col
            className={classNames('sidepanelItem-content', isChild && 'sidepanelItem-childPadding')}
            sm={6}
            dataTestAttrs={{component: 'SidepanelItem', element: 'contentCol'}}
          >
            {isExpandable && (
              <div className="sidepanelItem-disclosureTriangle">
                <BenchlingIcon icon={isExpanded ? 'triangle-down' : 'triangle-right'} />
              </div>
            )}
            {icon && <div className="sidepanelItem-icon">{icon}</div>}
            <div className="sidepanelItem-text">
              <div className="u-lr-split">
                <span
                  className={classNames(
                    'sidepanelItem-title',
                    'u-overflow-wrap-break-word',
                    css(styles.title),
                    {
                      'u-ellipsis-text': isCompact,
                    }
                  )}
                  id={titleId}
                  {...getTestProps({component: 'SidepanelItem', element: 'title'})}
                >
                  <Tooltip title={title.text}>{title.node || <span>{title.text}</span>}</Tooltip>
                </span>
                {inlineElement}
              </div>
              {!isCompact && <div>{subHeaderElement}</div>}
            </div>
          </nyc.Col>
        </nyc.Row>
      </div>
    </TooltipWrapper>
  );
}

export default SidepanelItem;

export const styles = StyleSheet.create({
  inlineElement: {
    display: 'flex',
    justifyContent: 'flex-end',
    minWidth: '50px',
  },
  title: {
    width: '100%',
    minWidth: 0,
  },
});
